"use client";

import { useState } from "react";
import {
	ChevronLeft,
	ChevronRight,
	Laptop,
	Wallpaper,
	Webcam,
	ZoomIn,
} from "lucide-react";

// Each tab shows a short looping demo clip. `video` points at the final file
// under /public/features/ — drop the real recording in at that path (overwrite
// the placeholder) and it appears, no code change needed. All four currently
// reuse hero.mp4 as a placeholder. To add a feature, just append an entry here.
const FEATURES = [
	{
		icon: Laptop,
		tag: "Device mockups",
		title: "Real device mockups",
		heading: "Wrap any site in a real device frame",
		body: "Drop your site into a pixel-perfect iPhone 17 Pro Max, MacBook Pro, iPad Pro, or Studio Display frame — with simulated iOS, macOS, and Windows browser chrome.",
		video: "/features/mockups.mp4",
	},
	{
		icon: Wallpaper,
		tag: "Backdrops",
		title: "Beautiful backdrops",
		heading: "Set the scene with gorgeous backdrops",
		body: "Frame your mockup against a huge library of backdrops — from the full set of macOS wallpapers to clean gradients and solid colors — so every demo looks studio-ready.",
		video: "/features/backdrops.mp4",
	},
	{
		icon: Webcam,
		tag: "Recording",
		title: "Webcam + mic recording",
		heading: "Record yourself with webcam + mic",
		body: "Drop in a draggable webcam bubble and record a microphone voiceover as you click through — all captured into one smooth HD video.",
		video: "/features/webcam.mp4",
	},
	{
		icon: ZoomIn,
		tag: "Zoom editor",
		title: "Cinematic zoom editor",
		heading: "Add cinematic zoom in the editor",
		body: "Add non-destructive zoom anywhere the cursor moved, fine-tune the timing as keyframes, and export to MP4 or WebM — Screen-Studio-style polish in your browser.",
		video: "/features/zoom.mp4",
	},
];

export function FeatureShowcase() {
	const [active, setActive] = useState(0);
	const current = FEATURES[active];

	const go = (dir: number) =>
		setActive((active + dir + FEATURES.length) % FEATURES.length);

	return (
		<div className="flex flex-col gap-10">
			{/* Arrows flank the whole video + text block on the section edges */}
			<div className="flex items-center gap-4 sm:gap-8 lg:gap-10">
				<button
					type="button"
					onClick={() => go(-1)}
					aria-label="Previous feature"
					className="border-border bg-background text-foreground hover:bg-accent hidden size-10 shrink-0 items-center justify-center rounded-full border shadow-sm transition hover:scale-105 sm:flex"
				>
					<ChevronLeft className="size-5" />
				</button>

				{/* Video + text side by side — video gets the wider column */}
				<div className="grid flex-1 items-center gap-8 lg:grid-cols-5 lg:gap-12">
					{/* Video — restarts on tab change via `key` */}
					<div className="border-border bg-background overflow-hidden rounded-2xl border shadow-xl lg:col-span-3">
						<video
							key={current.video}
							src={current.video}
							className="h-auto w-full object-contain"
							autoPlay
							loop
							muted
							playsInline
							poster="/hero1.webp"
							aria-label={`${current.heading} demo`}
						/>
					</div>

					{/* Active feature text — beside the video.
					    All four blocks stay in the DOM for SEO; inactive ones are
					    hidden with CSS only (display:none), never removed. */}
					<div className="lg:col-span-2">
						{FEATURES.map((feat, i) => (
							<div key={feat.title} className={i === active ? "" : "hidden"}>
								<span className="text-[#9D5CE0] text-sm font-semibold tracking-wide uppercase">
									{feat.tag}
								</span>
								<h3 className="mt-2 text-2xl font-bold tracking-tight sm:text-3xl">
									{feat.heading}
								</h3>
								<p className="text-muted-foreground mt-4 text-base leading-relaxed">
									{feat.body}
								</p>
							</div>
						))}
					</div>
				</div>

				<button
					type="button"
					onClick={() => go(1)}
					aria-label="Next feature"
					className="border-border bg-background text-foreground hover:bg-accent hidden size-10 shrink-0 items-center justify-center rounded-full border shadow-sm transition hover:scale-105 sm:flex"
				>
					<ChevronRight className="size-5" />
				</button>
			</div>

			{/* Horizontal selector cards — wrap as more features are added */}
			<div className="flex flex-wrap justify-center gap-3">
				{FEATURES.map((feat, i) => {
					const Icon = feat.icon;
					const isActive = i === active;
					return (
						<button
							key={feat.title}
							type="button"
							onClick={() => setActive(i)}
							aria-pressed={isActive}
							className={`flex items-center gap-2.5 rounded-xl border px-4 py-3 text-sm font-medium transition-all ${
								isActive
									? "border-black bg-[#813BBF]/5 text-foreground shadow-sm dark:border-white"
									: "border-border bg-background text-muted-foreground hover:border-black/40 hover:text-foreground dark:hover:border-white/40"
							}`}
						>
							<span
								className={`flex size-7 shrink-0 items-center justify-center rounded-lg transition-colors ${
									isActive
										? "bg-[#813BBF] text-white"
										: "bg-[#813BBF]/10 text-[#9D5CE0]"
								}`}
							>
								<Icon className="size-4" />
							</span>
							{feat.title}
						</button>
					);
				})}
			</div>
		</div>
	);
}
